import { isValid } from 'date-fns';

import Humanograma from 'components/Humanograma';
import { UserAvatar } from 'components/user-avatar';
import { sanitizeHtml } from 'utils/sanitizeHtml';
import { BehaviorLabel } from 'utils/strings';

import { dataCurta } from '../data-curta';

/** Item de `getKPIPublicHistoryByTime` (`find/public_extrato`), com o que o perfil usa. */
export interface ReconhecimentoDoExtrato {
  _id?: string;
  sender: string;
  sender_name?: string;
  sender_funcao?: string;
  sender_prefixo?: number | string;
  sender_nome_dependencia?: string;
  sender_avatar?: Record<string, unknown>;
  behavior?: string;
  time?: number | string;
  message?: string;
  cancelled?: boolean;
}

interface ReconhecimentoRecebidoProps {
  reconhecimento: ReconhecimentoDoExtrato;
}

/**
 * Reconhecimento no perfil de Destaques (`Galeria.dc.html`, DST-24): quem enviou, o comportamento, a data e a
 * mensagem. O tipo (#UAU, #MANDOUBEM, #VALEU) do design não aparece: o `public_extrato` não traz a moeda (DST-L01).
 */
export default function ReconhecimentoRecebido({ reconhecimento }: ReconhecimentoRecebidoProps) {
  const primeiroNome = String(reconhecimento.sender_name ?? '').split(' ')[0];
  const comportamento = reconhecimento.behavior ? BehaviorLabel[reconhecimento.behavior] : undefined;
  const data = new Date(reconhecimento.time ?? NaN);

  return (
    // `div`, e não `article`: no perfil cada reconhecimento já é um item de lista, e vários `article` sem rótulo único
    // reprovam no IBM (`aria_article_label_unique`).
    <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-solid border-line bg-surface-0 px-[18px] py-4 text-content">
      <div className="flex items-start gap-2.5">
        <span aria-hidden="true" className="h-10 w-10 shrink-0 overflow-hidden rounded-jj-full bg-brand-purple-light">
          <UserAvatar data={reconhecimento.sender_avatar ?? {}} responsive />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
          <div className="text-[13px] text-content">
            <Humanograma
              data={{
                ...reconhecimento,
                nome: reconhecimento.sender_name,
                funcao: reconhecimento.sender_funcao,
                _id: reconhecimento.sender,
                prefixo: reconhecimento.sender_prefixo,
                nome_dependencia: reconhecimento.sender_nome_dependencia,
              }}
            >
              {/* Sem `tabIndex`: o foco é do link do Humanograma, que abre a dica. */}
              <span>
                <b className="font-display text-sm font-bold text-brand-blue-dark">{primeiroNome}</b> ({reconhecimento.sender})
              </span>
            </Humanograma>{' '}
            enviou
          </div>
          {comportamento && (
            <span className="self-start rounded-jj-pill bg-brand-cyan-light px-2.5 py-[3px] text-[11px] font-bold text-content">
              {comportamento}
            </span>
          )}
        </div>

        {reconhecimento.time && isValid(data) && (
          <time dateTime={data.toISOString()} className="shrink-0 whitespace-nowrap text-[11px] text-content">
            {dataCurta(data)}
          </time>
        )}
      </div>

      {!!reconhecimento.message && (
        <div className="flex flex-col gap-1 border-0 border-t border-solid border-line pt-3">
          <span className="font-display text-xs font-bold text-brand-blue">Mensagem recebida</span>
          <div
            className="text-sm leading-normal text-content [overflow-wrap:anywhere]"
            // Texto digitado por outro funci: passa pelo sanitizador antes de virar HTML.
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(reconhecimento.message) }}
          />
        </div>
      )}
    </div>
  );
}
